/**
 * Stato "in pausa" di un turno e regola di ripresa dopo lo Stop.
 *
 * Stessa logica dell'app. Un turno con Start e Stop ma senza report è "in pausa"
 * e si può riprendere solo nel giorno del turno (o il giorno dopo se notturno);
 * dopo resta solo l'invio del report. Usato sia dal bottone "Riprendi" sia dal badge.
 */
export interface TurnoStatoLike {
    effettivoInizio?: string | null;
    effettivoFine?: string | null;
    reportTrasmesso?: boolean | null;
    dataTurno?: string | null;
    notturno?: boolean | null;
}

const dayStr = (d: Date): string => {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/** Turno avviato e stoppato ma senza report finale inviato. */
export const isTurnoInPausa = (t: TurnoStatoLike): boolean =>
    !!t.effettivoInizio && !!t.effettivoFine && !t.reportTrasmesso;

/**
 * Se, ad oggi, un turno in pausa può ancora essere ripreso (stesso giorno del
 * turno, o dataTurno+1 se notturno). Oltre → solo report.
 */
export const canResumeTurnoAfterPause = (t: TurnoStatoLike, now: Date = new Date()): boolean => {
    const today = dayStr(now);
    const dataTurnoStr = t.dataTurno ? String(t.dataTurno).slice(0, 10) : '';
    const isNotturno = !!t.notturno;
    const finalDayStr = (() => {
        if (!isNotturno || !dataTurnoStr) return dataTurnoStr;
        const [y, m, d] = dataTurnoStr.split('-').map(Number);
        const dt = new Date(y, m - 1, d);
        dt.setDate(dt.getDate() + 1);
        return dayStr(dt);
    })();
    return isNotturno
        ? (!!finalDayStr && today <= finalDayStr)
        : (!!dataTurnoStr && today <= dataTurnoStr);
};

/**
 * Stato di pausa ai fini del badge:
 *  - 'PAUSA'  → in pausa, ripresa ancora possibile (stesso giorno)
 *  - 'REPORT' → in pausa dal/dai giorno/i precedente/i, solo invio report
 *  - null     → non è in pausa
 */
export const turnoPausaStato = (t: TurnoStatoLike, now?: Date): 'PAUSA' | 'REPORT' | null => {
    if (!isTurnoInPausa(t)) return null;
    return canResumeTurnoAfterPause(t, now) ? 'PAUSA' : 'REPORT';
};

/**
 * Turno "non avviato": giorno di fine passato e nessuno start reale.
 * Gli stati terminali (rifiutato/revocato) li gestisce il chiamante.
 */
export const isTurnoNonAvviato = (t: TurnoStatoLike, now: Date = new Date()): boolean => {
    if (t.effettivoInizio) return false;
    const dataTurnoStr = t.dataTurno ? String(t.dataTurno).slice(0, 10) : '';
    if (!dataTurnoStr) return false;
    const finalDayStr = (() => {
        if (!t.notturno) return dataTurnoStr;
        const [y, m, d] = dataTurnoStr.split('-').map(Number);
        const dt = new Date(y, m - 1, d);
        dt.setDate(dt.getDate() + 1);
        return dayStr(dt);
    })();
    return dayStr(now) > finalDayStr;
};
